/**
 * Story features: the feature-toggle map on the article instance behind a
 * story, reachable only through the `stories` resource.
 *
 * Split out of `./stories.ts` rather than living beside the rest of that
 * resource for one reason: `useInstanceConfig` needs this read, and
 * `./stories.ts` imports `util/datastore` at runtime for its context-reading
 * hooks — so importing it from there would close a cycle.
 * Every export here takes its identity as an argument and imports nothing from
 * `util/datastore` at runtime. The context-reading wrappers live in
 * `util/features-hooks.ts`, which is also where the choice between this
 * resource, `spaces`, and `instanceFeatures` is made.
 */
import type { RouterUtils } from '@orpc/react-query';
import type { IsoLocale } from '@repo/shared/locale';
import type { Story } from '@repo/shared/orpc/procedures/stories';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useApiQueryUtils } from 'system/api-client-context';
import type { ApiClient } from 'system/orpc';
import type { FeaturesQueryOptions } from 'util/features';
import { fireAndForget } from 'util/hooks';

/**
 * Which story a feature map belongs to, and the locale the read is scoped to.
 * `locale` is part of it because `stories.get` takes one and a query key is
 * matched exactly: a key built without it addresses a different cache entry
 * than the one `useStory` fills, so the two would fetch the same story twice
 * and an optimistic patch here would miss the entry a reader is rendering.
 */
export type StoryIdentity = { siloKey: string; storyKey: string; locale: IsoLocale };

/**
 * Which features a story has explicitly toggled, `null` while the read is in
 * flight and for a story with none set. Only explicit toggles come back — a
 * caller merges the structure and deployment defaults underneath, which
 * `useEnabledFeatures` does.
 *
 * `enabled: false` is how a caller says "this instance is not a story": the
 * query never runs and `features` stays `null`, so a non-article instance costs
 * no request.
 *
 * `isResolved` means there is nothing left to wait for: the read succeeded, or
 * it never ran at all. It carries the same contract as
 * `useInstanceFeaturesQuery`, since a caller gating a story tree on this read
 * (the test harness, in place of a route layout's suspense) needs the same
 * signal from whichever of the two applies. A *failed* read is deliberately
 * not resolved, so that gate holds rather than assembling a screen against the
 * defaults alone.
 */
export function useStoryFeaturesQuery(
    { siloKey, storyKey, locale }: StoryIdentity,
    { enabled = true }: { enabled?: boolean } = {},
): { features: Record<string, boolean> | null; isResolved: boolean } {
    const utils = useApiQueryUtils();
    const queryEnabled = enabled && !!storyKey;
    const query = useQuery({ ...storyQueryOptions(utils, { siloKey, storyKey, locale }), enabled: queryEnabled });
    return { features: query.data?.features ?? null, isResolved: !queryEnabled || query.isSuccess };
}

/**
 * The story `FeaturesSource`: a story's toggles are a field of the stories
 * resource, and its `instanceKey` is the story's `storyKey`. Normalized to a
 * plain `Record<string, boolean>` via `select`. Same query key as
 * `useStoryFeaturesQuery`, so every observer shares one cache entry and any
 * update to it — an optimistic write, an invalidation — reaches them all. A
 * story is constructed on first read, so there is no not-found answer — a
 * failed read is an error. `select` runs per observer, so this doesn't change
 * what any other consumer of the key reads.
 */
export function storyFeaturesQueryOptions(
    utils: RouterUtils<ApiClient>,
    { siloKey, instanceKey, locale }: { siloKey: string; instanceKey: string; locale: IsoLocale },
): FeaturesQueryOptions {
    return {
        ...storyQueryOptions(utils, { siloKey, storyKey: instanceKey, locale }),
        select: (story: Story): Record<string, boolean> => story.features ?? {},
    };
}

/**
 * Canonical `stories.get` query options: every observer of a story's cache
 * entry builds on these, so the entry behaves one way everywhere. Retries are
 * off: a failed read ran a server-side fetch of the source page, and another
 * attempt re-runs that fetch for an answer that won't change.
 */
export function storyQueryOptions(utils: RouterUtils<ApiClient>, { siloKey, storyKey, locale }: StoryIdentity) {
    return {
        ...utils.stories.get.queryOptions({ input: { siloKey, storyKey, locale } }),
        retry: false,
    } as const;
}

/**
 * Turn features on or off on one story, leaving the rest of the map alone.
 * Takes a patch, so a pick-one teaser variant can flip the old key off and the
 * new one on in one write and the server merges both.
 *
 * Optimistic in TanStack's `onMutate` / `onError` / `onSettled` shape, because
 * the config layer reads this same query: without the patch, every toggle would
 * blank the screen for a round trip. Only `stories.get` is invalidated — the
 * story list carries no features, so it cannot go stale from this write.
 */
export function useSetStoryFeatures({ siloKey, storyKey, locale }: StoryIdentity) {
    const api = useApiClient();
    const utils = useApiQueryUtils();
    const queryClient = useQueryClient();
    const queryKey = utils.stories.get.queryOptions({ input: { siloKey, storyKey, locale } }).queryKey;

    const mutation = useMutation({
        mutationFn: (features: Record<string, boolean>) => api.stories.setFeatures({ siloKey, storyKey, locale, features }),
        onMutate: async (features: Record<string, boolean>) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<Story>(queryKey);
            if (previous) {
                queryClient.setQueryData(queryKey, { ...previous, features: { ...(previous.features ?? {}), ...features } });
            }
            return { previous };
        },
        onError: (_error, _features, context) => {
            queryClient.setQueryData(queryKey, context?.previous);
        },
        onSettled: () => fireAndForget(queryClient.invalidateQueries({ queryKey })),
    });
    return { ...mutation, setStoryFeaturesAsync: mutation.mutateAsync };
}
